import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Bulk sub-task search index for a shared list.
 *
 * Fetches every sub-task in the list in one request (sub-tasks are otherwise
 * lazy-loaded per parent on row-expand, so they aren't in the main item list)
 * and returns a `parentItemId → concatenated searchable text` map. Search folds
 * that text into the parent task's haystack, so a matching sub-task surfaces its
 * parent. Gated by `enabled` — pass `searchActive` so nothing is fetched until
 * the user actually searches; the result is cached for the rest of the session.
 */
export function useTodoSubtaskIndex(holderId: string | null, listId: string, enabled = true) {
  const { data } = useQuery({
    queryKey: queryKeys.todoLists.subtaskIndex(holderId ?? '', listId),
    queryFn: () => api.listTodoSubtaskIndex(holderId as string, listId),
    enabled: enabled && !!holderId && !!listId,
    staleTime: 30_000,
  });

  return useMemo(() => {
    const byParent = new Map<string, string>();
    for (const s of data?.subtasks ?? []) {
      const text = [s.title, s.description].filter(Boolean).join(' ');
      const prev = byParent.get(s.parentItemId);
      byParent.set(s.parentItemId, prev ? `${prev} ${text}` : text);
    }
    return byParent;
  }, [data]);
}
